import { useQuery } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Clock,
  User,
  Activity,
  CheckCircle,
  XCircle,
  Pause,
  RotateCcw,
  Square
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { api } from '../lib/api'
import { formatTimestamp, formatDuration } from '../lib/utils'

// Status configurations
const STATUS_CONFIG = {
  queued: { 
    icon: Clock, 
    color: 'bg-gray-100 text-gray-800 border-gray-200', 
    label: 'Queued' 
  },
  running: { 
    icon: Activity, 
    color: 'bg-blue-100 text-blue-800 border-blue-200', 
    label: 'Running' 
  },
  success: { 
    icon: CheckCircle, 
    color: 'bg-green-100 text-green-800 border-green-200', 
    label: 'Success' 
  },
  failed: { 
    icon: XCircle, 
    color: 'bg-red-100 text-red-800 border-red-200', 
    label: 'Failed' 
  },
  cancelled: { 
    icon: Pause, 
    color: 'bg-orange-100 text-orange-800 border-orange-200', 
    label: 'Cancelled' 
  },
}

export function WorkflowRunDetail() {
  const { id } = useParams<{ id: string }>()
  
  const { data: run, isLoading, refetch } = useQuery({
    queryKey: ['workflow-run', id],
    queryFn: () => api.getWorkflowRun(Number(id)),
    enabled: !!id,
  })

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]
    if (!config) return null
    
    const Icon = config.icon
    return (
      <Badge variant="outline" className={`${config.color} border`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    )
  }

  const handleRetry = async () => {
    if (!id) return
    try {
      await api.retryWorkflowRun(Number(id))
      refetch()
    } catch (error) {
      console.error('Failed to retry workflow run:', error)
    }
  }

  const handleCancel = async () => {
    if (!id) return
    try {
      await api.cancelWorkflowRun(Number(id))
      refetch()
    } catch (error) {
      console.error('Failed to cancel workflow run:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!run) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium mb-2">Workflow Run Not Found</h3>
        <p className="text-muted-foreground mb-4">
          The workflow run you're looking for doesn't exist or you don't have access to it.
        </p>
        <Link to="/runs">
          <Button>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Runs
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/runs">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Runs
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Workflow Run #{run.id}
            </h1>
            <p className="text-muted-foreground">
              {run.workflow_name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {run.status === 'failed' && (
            <Button onClick={handleRetry}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          )}
          {run.status === 'running' && (
            <Button variant="destructive" onClick={handleCancel}>
              <Square className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Status and Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {getStatusBadge(run.status)}
              {run.error_message && (
                <div className="text-sm text-red-600 mt-2">
                  {run.error_message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Timing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">Started:</span>
                <div>{run.started_at ? formatTimestamp(new Date(run.started_at).getTime()) : 'Not started'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Finished:</span>
                <div>{run.finished_at ? formatTimestamp(new Date(run.finished_at).getTime()) : 'Not finished'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Duration:</span>
                <div>{run.duration_ms ? formatDuration(run.duration_ms) : 'N/A'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">Trigger:</span>
                <div className="capitalize">{run.trigger}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Created by:</span>
                <div>{run.created_by_email || 'Unknown'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Created:</span>
                <div>{formatTimestamp(new Date(run.created_at).getTime())}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Steps */}
      {run.steps && run.steps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Workflow Steps</CardTitle>
            <CardDescription>
              Steps configured for this workflow execution
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {run.steps.map((step: any, index: number) => (
                <div key={step.id} className="flex items-start gap-4 p-4 border rounded-lg">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium capitalize">
                      {step.step_type.replace(/_/g, ' ')}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {step.params && Object.keys(step.params).length > 0 && (
                        <pre className="text-xs bg-muted p-2 rounded mt-2 overflow-x-auto">
                          {JSON.stringify(step.params, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                  <Badge variant={step.enabled ? 'default' : 'secondary'}>
                    {step.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result Data */}
      {run.result_data && (
        <Card>
          <CardHeader>
            <CardTitle>Execution Results</CardTitle>
            <CardDescription>
              Data returned from the workflow execution
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-4 rounded overflow-x-auto">
              {typeof run.result_data === 'string' 
                ? run.result_data 
                : JSON.stringify(run.result_data, null, 2)
              }
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
